// MobileHqGob — the MP starting unit: a passive MobileUnit (aggressiveness stripped, no
// weapon) that can transform into a Headquarters. game/MobileHQ.cpp (all 200 lines) +
// IsTileFree (game/misc.cpp:112-158).

import type { SimContext } from '../context.ts';
import { Gob } from './gob.ts';
import { MobileUnitGob, kfMuntAggressivenessBits } from './mobileunit.ts';
import { MobileUnitConsts, InitMobileUnitConstsClass, StructConsts } from './unitconsts.ts';
import { kgtMobileHeadquarters, kgtHeadquarters } from './gobtypes.ts';
import { kutMobileHeadquarters, kutHeadquarters } from '../player/side.ts';
import { kumResearchCenter } from '../player/masks.ts';
import { kupgmAdvancedVTS } from '../player/side.ts';
import { kbfReserved, kbfStructure } from '../terrain/terrainmap.ts';
import {
  Message,
  knHandled,
  knDeleted,
  kstReservedGlobal,
  kmidTransformCommand,
  kmidAttackCommand,
} from '../sm/message.ts';
import { IsTileFree } from './tilesearch.ts';
import { CreateGob } from './factory.ts';
import { UnitGob } from './unitgob.ts';
import { divfx, mulfx } from '../math/fix.ts';
import { WcFromTc } from '../math/coords.ts';
import type { Player } from '../player/player.ts';
import type { TPoint } from '../types.ts';
import type { IniReader } from '../data/ini.ts';
import {
  ksfxNothing,
  ksfxMobileHeadquartersDeploy,
  ksfxcVehicleDestroyed,
  ksfxcMale01Select,
  ksfxcMale01Move,
  ksfxcMale01Attack,
} from '../data/soundeffects.ts';
import type { FogMap } from '../sim/fogmap.ts';

// game/MobileHQ.cpp:14
const s_anMovingStripIndices = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];

// game/MobileHQ.cpp:16-40 — the file-static gConsts becomes a ctx-registered consts
// (no module singletons). Sfx category ids are host-only and bind with the audio host.
export function InitMobileHqClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new MobileUnitConsts().bind(ctx);
  c.gt = kgtMobileHeadquarters;
  c.ut = kutMobileHeadquarters;
  c.umPrerequisites = kumResearchCenter;
  c.upgmPrerequisites = kupgmAdvancedVTS;

  c.anFiringStripIndices = s_anMovingStripIndices;
  c.anMovingStripIndices = s_anMovingStripIndices;
  c.anIdleStripIndices = s_anMovingStripIndices;

  // Sound effects (MobileHQ.cpp:31-37)
  c.sfxFire = ksfxNothing;
  c.sfxImpact = ksfxNothing;
  c.sfxcDestroyed = ksfxcVehicleDestroyed;
  c.sfxcSelect = ksfxcMale01Select;
  c.sfxcMove = ksfxcMale01Move;
  c.sfxcAttack = ksfxcMale01Attack;
  return InitMobileUnitConstsClass(ctx, c, pini);
}

export class MobileHqGob extends MobileUnitGob {
  // game/MobileHQ.cpp:47-50
  constructor(ctx: SimContext) {
    super(ctx, ctx.unitConsts[kutMobileHeadquarters] as MobileUnitConsts);
    this.m_wfMunt &= ~kfMuntAggressivenessBits;
  }

  // game/MobileHQ.cpp:52-107 — needs the HQ footprint (3x2) free of reserved/structure
  // tiles and galaxite. The fog map is the galaxite store.
  CanTransform(pfogm: FogMap): { can: boolean; tpt: TPoint } {
    const bldcHq = this.ctx.unitConsts[kutHeadquarters] as StructConsts;

    const tpt: TPoint = { tx: 0, ty: 0 };
    this.GetTilePosition(tpt);
    let tx = tpt.tx - 1;
    let ty = tpt.ty - 1;
    if (tx < 0) tx = 0;
    if (ty < 0) ty = 0;
    let txR = tx + bldcHq.ctxTiles;
    let tyB = ty + bldcHq.ctyTiles;

    const { ctx: cxMap, cty: cyMap } = this.ctx.gobm.GetMapSize();
    if (txR > cxMap) {
      txR = cxMap;
      tx = txR - bldcHq.ctxTiles;
    }
    if (tyB > cyMap) {
      tyB = cyMap;
      ty = tyB - bldcHq.ctyTiles;
    }
    const tptDest: TPoint = { tx, ty };

    let fOccupied = false;
    const txT = tx;
    for (; ty < tyB; ty++) {
      for (tx = txT; tx < txR; tx++) {
        const r = IsTileFree(this.ctx, tx, ty, kbfReserved | kbfStructure);
        if (!r.free) {
          if (r.pgob !== (this as Gob)) {
            fOccupied = true;
            break;
          }
        }
        if (pfogm.GetGalaxite(tx, ty) !== 0) {
          fOccupied = true;
          break;
        }
      }
    }

    return { can: !fOccupied, tpt: tptDest };
  }

  // game/MobileHQ.cpp:138-199 — Transform/Attack arms; everything else inherits
  // MobileUnitGob (the hand-written EndStateMachineInherit expansion).
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstReservedGlobal) {
      if (pmsg.mid === kmidTransformCommand) {
        // Lag means the wanted space may no longer be free: re-check, fail quietly
        const r = this.CanTransform(this.ctx.fogm!);
        if (!r.can) return knHandled;

        this.Deactivate();

        this.ctx.gobm.RemoveGob(this);

        if (this.m_pplr === this.ctx.plrLocal) this.ctx.host.PlaySfx(ksfxMobileHeadquartersDeploy); // MobileHQ.cpp:164

        // Create the Headquarters, carrying the MHQ's health forward proportionally
        const pgobHq = CreateGob(this.ctx, kgtHeadquarters) as UnitGob | null;
        if (pgobHq !== null) {
          const fxHealth = divfx(
            mulfx(
              this.m_fxHealth,
              (this.ctx.unitConsts[kutHeadquarters] as StructConsts).GetArmorStrength(),
            ),
            this.m_puntc.GetArmorStrength(),
          );
          pgobHq.Init(WcFromTc(r.tpt.tx), WcFromTc(r.tpt.ty), this.m_pplr as Player, fxHealth, 0);
        }

        return knDeleted; // self removed; GC collects
      }

      if (pmsg.mid === kmidAttackCommand) {
        // MobileHQs have no attack: swallow the auto-response impulse
        return knHandled;
      }

      return super.ProcessStateMachineMessage(st, pmsg);
    }
    return super.ProcessStateMachineMessage(st, pmsg);
  }
}

// self-register (see factory.ts)
import { registerGobType } from './factory.ts';
registerGobType(kgtMobileHeadquarters, (ctx) => new MobileHqGob(ctx));
